// ---------- les arguments d'un pack ----------
//
// Ce qui donne envie de débloquer un pack, en une liste FERMÉE de slugs plutôt qu'en texte
// rédigé. La raison est unique et elle décide de tout ce fichier : l'application est
// internationale. Un argument est traduit une fois dans les dictionnaires, et une langue de plus
// ne coûte alors rien par pack — là où une description écrite devrait être réécrite dans chaque
// langue, pour chaque pack, à chaque retouche.
//
// Le prix est assumé : on ne peut pas dire n'importe quoi d'un pack, seulement choisir parmi
// neuf phrases. Ajouter la dixième demande deux lignes de dictionnaire et une publication de
// l'application — exactement le coût qu'ont déjà les libellés de catégorie et de difficulté.
//
// TROIS DES NEUF NE SE COCHENT PAS. « Une seule technique », « techniques variées » et « en
// couleurs » se lisent dans la composition par alphabet que la route renvoie déjà : les demander
// à l'administration serait lui faire ressaisir ce que la base sait. Ils sont donc DÉDUITS ici,
// et l'administration ne coche que les six qui relèvent d'un jugement éditorial.

import { techniqueFamily } from './techniqueIcon';
import { t } from './i18n';
import type { PackSummary } from './api';

/** L'ordre de ce tableau est l'ordre d'affichage : les arguments éditoriaux d'abord — ce sont
 *  eux qui portent une intention — les déduits ensuite. Seuls les trois premiers de la liste
 *  finale sont affichés (cf. packArguments), donc cet ordre est aussi une priorité. */
const ORDER = [
  'debutant',
  'portraits',
  'patience',
  'nouveau',
  'costaud',
  'express',
  'mono',
  'varie',
  'couleur',
] as const;

export type PackArgumentId = (typeof ORDER)[number];

/** Les six que l'administration coche. Les trois autres (`mono`, `varie`, `couleur`) sont
 *  déduits et ne doivent PAS apparaître dans le formulaire : cochés à la main, ils pourraient
 *  contredire la composition réelle du pack. */
export const EDITORIAL: readonly PackArgumentId[] = [
  'debutant',
  'portraits',
  'patience',
  'nouveau',
  'costaud',
  'express',
];

const LABELS: Record<PackArgumentId, string> = {
  debutant: t('packs.arg.debutant'),
  portraits: t('packs.arg.portraits'),
  patience: t('packs.arg.patience'),
  nouveau: t('packs.arg.nouveau'),
  costaud: t('packs.arg.costaud'),
  express: t('packs.arg.express'),
  mono: t('packs.arg.mono'),
  varie: t('packs.arg.varie'),
  couleur: t('packs.arg.couleur'),
};

const KNOWN = new Set<string>(ORDER);

/** Ce que la base a stocké, nettoyé. Le champ est du JSON écrit par l'administration : il peut
 *  être absent, mal formé, ou porter un slug d'une version plus récente de l'application que
 *  celle qui l'affiche. Aucun de ces cas n'est une erreur à signaler — on garde ce qu'on sait
 *  nommer et on jette le reste, ce qui est exactement le traitement qu'un slug de catégorie
 *  inconnu reçoit déjà. */
function stored(raw: string | null): PackArgumentId[] {
  if (!raw) return [];
  try {
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.filter((v): v is PackArgumentId => typeof v === 'string' && KNOWN.has(v));
  } catch {
    return [];
  }
}

/** Ceux que la composition du pack suffit à établir. Trois, et pas un de plus : tout ce qui
 *  demanderait la taille des grilles n'est pas déductible ici, puisque la route ne renvoie que
 *  le compte et la répartition par alphabet — `costaud` et `express` restent donc des cases à
 *  cocher. */
function deduced(p: PackSummary): PackArgumentId[] {
  const out: PackArgumentId[] = [];
  const families = new Set(p.alphabets.map((a) => techniqueFamily(a.alphabetId)));
  if (p.alphabets.length === 1) out.push('mono');
  else if (families.size >= 3) out.push('varie');
  if (families.has('pointille')) out.push('couleur');
  return out;
}

/**
 * Les arguments d'un pack, prêts à afficher : les cochés puis les déduits, dans l'ordre de
 * ORDER, sans doublon, et TROIS AU MAXIMUM.
 *
 * Trois, parce qu'au-delà ce n'est plus un argument mais une liste — et une liste ne donne pas
 * envie, elle se saute. La coupe tombe sur les déduits en dernier, ce qui est le bon ordre : un
 * argument choisi par un humain vaut mieux qu'un argument calculé.
 */
export function packArguments(p: PackSummary): string[] {
  const all = [...new Set([...stored(p.arguments), ...deduced(p)])];
  return all
    .sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))
    .slice(0, 3)
    .map((id) => LABELS[id]);
}

/** Le libellé d'un argument, pour l'administration (qui affiche la liste à cocher). */
export function argumentLabel(id: PackArgumentId): string {
  return LABELS[id];
}
